import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { switchMap, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { CrearProcesoComponent } from 'src/app/components/crear-proceso/crear-proceso.component';
import { ActionService, AuthService, SimulationService, UserService } from 'src/app/services';
import { Process, Simulation, User } from 'src/app/models';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit {
  simulations: Simulation[] = [];
  filteredSimulations: Simulation[] = [];
  user!: User;
  processes: Process[] = [];
  simulationForm: UntypedFormGroup;
  filteredUsers: { [key: string]: string[] } = {};
  searchValue = '';
  allUsers: string[] = [];
  selectCategory = '';
  category_selected = '';

  categoryMap: { [key: string]: string } = {
    '1': 'Monitorio',
    '2': 'Ejecutivo',
    '3': 'Sumario',
    '4': 'Ordinario',
    '5': 'Voluntario',
  };

  private subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialog,
    private simulationService: SimulationService,
    private userService: UserService,
    private authService: AuthService,
    private router: Router,
    private actionService: ActionService,
    private fb: UntypedFormBuilder
  ) {
    this.simulationForm = this.fb.group({
      process: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    console.log('HomeComponent ngOnInit iniciado');
    
    const userSub = this.userService.userHome$.subscribe((user) => {
      console.log('Usuario recibido en HomeComponent:', user);
      this.user = user;

      const simObservable = this.user.role === 'tutor'
        ? this.simulationService.getSimulations()
        : this.simulationService.getSimulationByUser(this.user.displayName!);

      simObservable.subscribe((simulations: Simulation[]) => {
        console.log('Simulaciones recibidas:', simulations);
        this.simulations = simulations;
        this.filteredSimulations = simulations;
        this.filterSimulations();
      }, (error: any) => {
        console.error('Error al cargar simulaciones:', error);
      });
    });
    this.subscriptions.push(userSub);

    const studentsSub = this.userService.getAllStudent().subscribe((usuarios) => {
      console.log('Estudiantes recibidos:', usuarios);
      this.allUsers = usuarios.map(user => user.displayName!).sort();
      this.initializeFilteredUsers();
    }, (error) => {
      console.error('Error al cargar estudiantes:', error);
    });
    this.subscriptions.push(studentsSub);

    const processControl = this.simulationForm.get('process');
    if (processControl) {
      const processSub = processControl.valueChanges.pipe(
        tap(() => this.simulationForm.get('action')?.reset('')),
        switchMap(process => this.actionService.getActionByProcess(process))
      ).subscribe();
      this.subscriptions.push(processSub);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  filterSimulations(): void {
    console.log('Filtrando con:', {
      searchValue: this.searchValue,
      category_selected: this.category_selected
    });

    this.filteredSimulations = this.simulations.filter(sim => {
      // Filtro por texto (nombre de la simulación)
      const matchesSearch = this.searchValue.trim() === '' ||
        sim.name?.toLowerCase().includes(this.searchValue.toLowerCase());

      // --- Lógica de filtro por categoría ---
      let simCategoryName: string | undefined;

      const rawSimCategory = sim.category;

      // Intenta obtener el nombre de la categoría usando el categoryMap si rawSimCategory es una clave numérica (o string numérica)
      if (rawSimCategory !== undefined && (typeof rawSimCategory === 'string' || typeof rawSimCategory === 'number') && this.categoryMap[String(rawSimCategory)]) {
        simCategoryName = this.categoryMap[String(rawSimCategory)];
      } else if (typeof rawSimCategory === 'string') {
        // Si rawSimCategory ya es un string (e.g., "Monitorio"), úsalo directamente.
        simCategoryName = rawSimCategory;
      } else {
        // Si la propiedad de la categoría es indefinida o no es una clave esperada, loggea para depurar.
        console.warn(
          `Advertencia: La simulación '${sim.name}' tiene un campo de categoría inesperado o indefinido.`,
          `Valor de la propiedad 'category' (o la que estés usando):`, rawSimCategory,
          `Objeto de simulación completo:`, sim
        );
      }

      // Filtro por categoría (compara el nombre normalizado de la simulación con el seleccionado)
      let matchesCategory = true;
      if (this.category_selected && this.category_selected !== '') {
        matchesCategory = simCategoryName === this.category_selected;
      }

      // Logs para depuración
      console.log(`Simulación ${sim.name}:`, {
        original_sim_category_property_value: rawSimCategory,
        category_value_used_for_comparison: simCategoryName,
        matchesSearch,
        matchesCategory
      });

      return matchesSearch && matchesCategory;
    });

    console.log('Simulaciones filtradas:', this.filteredSimulations.length);
  }

  initializeFilteredUsers(): void {
    ['juez', 'ab_demandante', 'ab_defensor', 'secretario'].forEach(field => {
      this.filteredUsers[field] = [...this.allUsers];
    });
  }

  redirectToVlex(): void {
    window.open('https://login.vlex.com/session/auth/active-directory', '_blank');
  }

  abrirDialog(): void {
    this.dialog.open(CrearProcesoComponent, {
      panelClass: 'dialog-process',
    });
  }

  logout(): void {
    Swal.fire({
      title: '¿Cerrar sesión?',
      text: 'Se cerrará tu sesión actual.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c0392b',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        this.authService.logout();
      }
    });
  }

  getUserInitials(name: string): string {
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 2][0]).toUpperCase();
    } else if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return '';
  }

  getAvatarColor(name: string): string {
    const baseColor = { r: 200, g: 168, b: 107 };
    let hash = 0;

    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const r = Math.max(0, Math.min(255, baseColor.r + (hash & 31) - 15));
    const g = Math.max(0, Math.min(255, baseColor.g + ((hash >> 5) & 31) - 15));
    const b = Math.max(0, Math.min(255, baseColor.b + ((hash >> 10) & 31) - 15));

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
}
